import * as ColorUtil from '../../../util/colors';
import {type ColorPaletteOption} from '../../../util/colors';
import {
  legendTemplateToFancyLegendProps,
  parseLegendTemplate,
} from '../../../util/legend';
import {prettifyMetricName} from '../../../util/plotHelpers/prettifyMetricName';
import {
  type Line,
  type LineProperties,
  type LineRun,
  type Mark,
} from '../../../util/plotHelpers/types';
import {type RunSetQuery} from '../../../util/queryTypes';
import * as Run from '../../../util/runs';
import {type Key} from '../../../util/runTypes';
import {type ExcludeOutliersValues} from '../../WorkspaceDrawer/Settings/types';
import {type LineConfig} from '../config/types';
import {type BucketedLine} from '../types';

export type LineTemplate = Omit<
  Line,
  | 'color'
  | 'data'
  | 'displayName'
  | 'fancyTitle'
  | 'history'
  | 'groupKeys'
  | 'mark'
  | 'meta'
  | 'runsetInfo'
  | 'run'
  | 'title'
  | 'type'
> & {
  color: (metricIndex: number) => Line['color'];
  data: null;
  fancyTitle: (prettyMetric: string) => Line['fancyTitle'];
  history: null;
  groupKeys: Key[];
  mark: (isAux: boolean) => Line['mark'] | undefined;
  meta: (agg: LineProperties['aggregation'], isAux: boolean) => Line['meta'];
  run: LineRun;
  runsetInfo: RunSetQuery | undefined;
  startTimeInMs: number;
  title: (metric: string) => string;
  type: (isAux: boolean) => Line['type'];
};

export function convertLineTemplateToLine(
  lineTemplate: LineTemplate,
  {
    agg,
    isAux,
    metric,
    metricIndex,
  }: {
    agg: LineProperties['aggregation'];
    isAux: boolean;
    metric: string;
    metricIndex: number;
  }
): Line {
  const {run, ...rest} = lineTemplate;

  // Don't include run in the spread to avoid type issues
  return {
    ...rest,
    aux: isAux,
    color: lineTemplate.color(metricIndex),
    data: [],
    fancyTitle: lineTemplate.fancyTitle(metric),
    mark: lineTemplate.mark(isAux),
    meta: lineTemplate.meta(
      agg.toLowerCase() as LineProperties['aggregation'],
      isAux
    ),
    metricName: metric,
    nanPoints: [],
    title: lineTemplate.title(metric),
    type: lineTemplate.type(isAux),
    run: lineTemplate.run,
  };
}

export type ConvertRunToLineTemplateParams = {
  colorPaletteOption: ColorPaletteOption;
  excludeOutliers?: ExcludeOutliersValues;
  groupKeys: Key[];
  lineConfig: Pick<
    LineConfig,
    | 'colorEachMetricDifferently'
    | 'customRunColors'
    | 'customRunNames'
    | 'entityName'
    | 'getLegendTemplateByRunsetId'
    | 'projectName'
    | 'rootUrl'
    | 'semanticLegendSettings'
    | 'groupArea'
  >;
  lineOpacity: number;
  run: BucketedLine;
};

export function convertRunToLineTemplate({
  colorPaletteOption,
  excludeOutliers,
  groupKeys,
  lineConfig,
  lineOpacity,
  run,
}: ConvertRunToLineTemplateParams): LineTemplate {
  const customRunName = Run.getCustomRunName(
    run.name,
    groupKeys.length > 0,
    lineConfig.customRunNames
  );

  // Prefer the run's own source project (in case of cross-project runs),
  // falling back to the runset's entity/project, and finally the panel's context.
  const entityForUrl =
    run.project?.entityName ??
    run.runsetInfo?.entityName ??
    lineConfig.entityName;
  const projectForUrl =
    run.project?.name ?? run.runsetInfo?.projectName ?? lineConfig.projectName;

  const runsetRootUrl =
    entityForUrl && projectForUrl
      ? `/${entityForUrl}/${projectForUrl}/runs`
      : lineConfig.rootUrl;

  return {
    ...run,
    color: (metricIndex: number) =>
      lineConfig.colorEachMetricDifferently
        ? ColorUtil.color(metricIndex, lineOpacity)
        : ColorUtil.runColor({
            run,
            groupKeys,
            // individual run lines still inherit their parent group color when
            // "use grouping from the runs table in charts" is off (groupKeys
            // empty). raw runset grouping is intentional here: it gates color
            // only, never line identity or aggregation
            colorGroupKeys: run.runsetInfo?.grouping,
            customRunColors: lineConfig.customRunColors,
            semanticLegendSettings: lineConfig.semanticLegendSettings,
            alpha: lineOpacity,
            colorPaletteOption,
          }),
    data: null,
    fancyTitle: (metric: string) =>
      legendTemplateToFancyLegendProps(
        lineConfig.getLegendTemplateByRunsetId(run.runsetId),
        run,
        groupKeys,
        prettifyMetricName(metric),
        runsetRootUrl ?? undefined,
        customRunName
      ),
    groupKeys,
    history: null,
    mark: (isAux: boolean) => (isAux ? undefined : ('solid' as Mark)),
    meta: (agg: LineProperties['aggregation'], isAux: boolean) => ({
      aggregation: agg,
      category: 'default',
      excludeOutliers: excludeOutliers,
      mode: 'full-fidelity',
      type: isAux ? 'area' : 'line',
    }),
    run: run,
    runsetInfo: undefined,
    // see notes on handling time plots
    // Readme: frontends/app/src/components/PanelRunsLinePlot/timePlots/readme.md
    // Demo: https://wandb.ai/public-team-private-projects/Time%20plots?nw=36sx43brn2r
    startTimeInMs: new Date(run.createdAt).getTime(),
    title: (metric: string) =>
      parseLegendTemplate(
        lineConfig.getLegendTemplateByRunsetId(run.runsetId),
        true,
        run,
        groupKeys,
        prettifyMetricName(metric),
        customRunName
      ),
    uniqueId: Run.uniqueId(run, groupKeys),
    type: (isAux: boolean) => (isAux ? 'area' : 'line'),
  };
}
